import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { TranslocoService } from '@ngneat/transloco';
import { HdBodygraphPlanetaryActivation, HdBodygraphRenderState } from '../../hd-bodygraph.models';
import { HD_BODYGRAPH_GATE_TO_CENTER } from '../../hd-bodygraph-metadata';
import { HdChartHoverHighlightService } from '../../hd-chart-hover-highlight.service';
import { HdContentLanguageService } from '../../hd-content-language.service';

interface LineCount {
  /** Stabiele, taal-onafhankelijke identiteit van de rij voor de stat-freeze. */
  key: string;
  line: number;
  count: number;
  selectedCount: number;
  /** Unieke poorten achter deze telling — de set die de stat-freeze bevriest. */
  gates: readonly number[];
}

@Component({
  selector: 'hd-line-stats',
  standalone: true,
  imports: [],
  templateUrl: './hd-line-stats.component.html',
  styleUrl: './hd-line-stats.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-stat-frozen]': 'frozenStatKey() !== null',
    '[class.is-dim-disabled]': 'dimDisabled()',
  },
})
export class HdLineStatsComponent {
  readonly planetaryActivations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly renderState = input.required<HdBodygraphRenderState>();
  readonly overallRenderState = input<HdBodygraphRenderState | null>(null);
  readonly selectedActivations = input<readonly HdBodygraphPlanetaryActivation[]>([]);
  readonly selectedSide = input<'design' | 'personality' | null>(null);
  readonly selectedColor = input<string | null>(null);

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });

  /** Stat-rijen zijn enkel klikbaar waar de hover/freeze-service actief is. */
  protected readonly statFreezeAvailable = computed(() => this.hoverService?.enabled() === true);

  protected readonly frozenStatKey = computed(() => this.hoverService?.frozenStatKey() ?? null);

  /**
   * De grid-kant dimt niet ("dimmen bij hover" uit, of "enkel één kant dimmen"
   * bij een grid-interactie zoals de stat-klik zelf): rijen dimmen dan niet
   * tijdens een stat-freeze.
   */
  protected readonly dimDisabled = computed(() =>
    this.hoverService != null && !this.hoverService.dimsGrid(),
  );

  protected readonly dormantStatKey = 'activation:dormant';
  protected readonly definedStatKey = 'activation:defined';

  /**
   * Vertaalde labels die de template gebruikt. Leest de effectieve HD
   * content-language (kan een override zijn t.o.v. de globale app-taal die
   * de Transloco-pipe volgt) zodat de settings-panel-taalwissel de stats
   * daadwerkelijk mee laat vertalen.
   */
  protected readonly labels = computed(() => {
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, lang);
    const ordinalLineSuffix: Record<number, string> = {};
    for (let line = 1; line <= 6; line++) {
      ordinalLineSuffix[line] = translate(`humanDesign.planetStats.ordinalLineSuffix.${line}`);
    }
    return {
      ordinalLineSuffix,
      lines: translate('humanDesign.planetStats.lines'),
      dormant: translate('humanDesign.planetStats.dormant'),
      defined: translate('humanDesign.planetStats.defined'),
      freezeRow: translate('humanDesign.planetStats.freezeRowTooltip'),
      unfreezeRow: translate('humanDesign.planetStats.unfreezeRowTooltip'),
    };
  });

  protected readonly lineCounts = computed<readonly LineCount[]>(() => {
    const activations = this.planetaryActivations();
    const overallCounts: Record<number, number> = {};
    const selectedCounts: Record<number, number> = {};
    const gatesByLine = new Map<number, Set<number>>();

    activations.forEach(activation => {
      if (activation.line != null) {
        overallCounts[activation.line] = (overallCounts[activation.line] || 0) + 1;
        const gates = gatesByLine.get(activation.line) ?? new Set<number>();
        gates.add(activation.gate);
        gatesByLine.set(activation.line, gates);
      }
    });
    this.selectedActivations().forEach(activation => {
      if (activation.line != null) {
        selectedCounts[activation.line] = (selectedCounts[activation.line] || 0) + 1;
      }
    });

    return Object.entries(overallCounts)
      .map(([line, count]) => ({
        key: `line:${line}`,
        line: Number(line),
        count,
        selectedCount: selectedCounts[Number(line)] ?? 0,
        gates: [...(gatesByLine.get(Number(line)) ?? [])].sort((a, b) => a - b),
      }))
      .sort((a, b) => b.count - a.count || a.line - b.line);
  });

  protected readonly activationStats = computed(() => {
    const overall = this.countActivationStats(
      this.planetaryActivations(),
      this.overallRenderState() ?? this.renderState(),
    );
    const selected = this.countActivationStats(this.selectedActivations(), this.renderState());

    return {
      dormant: { count: overall.dormant, selectedCount: selected.dormant, gates: overall.dormantGates },
      defined: { count: overall.defined, selectedCount: selected.defined, gates: overall.definedGates },
    };
  });

  protected isStatClickable(gates: readonly number[]): boolean {
    return this.statFreezeAvailable() && gates.length > 0;
  }

  protected isStatFrozen(key: string): boolean {
    return this.frozenStatKey() === key;
  }

  protected statTooltip(key: string): string {
    return this.isStatFrozen(key) ? this.labels().unfreezeRow : this.labels().freezeRow;
  }

  protected onStatClick(key: string, gates: readonly number[]): void {
    if (!this.isStatClickable(gates)) return;
    this.hoverService?.toggleStatFreeze(key, gates);
  }

  private countActivationStats(
    activations: readonly HdBodygraphPlanetaryActivation[],
    renderState: HdBodygraphRenderState,
  ): {
    dormant: number;
    defined: number;
    dormantGates: readonly number[];
    definedGates: readonly number[];
  } {
    let dormant = 0;
    let defined = 0;
    const dormantGates = new Set<number>();
    const definedGates = new Set<number>();

    activations.forEach(activation => {
      const center = HD_BODYGRAPH_GATE_TO_CENTER[activation.gate];
      if (center && renderState.centerStates[center].isDefined) {
        defined++;
        definedGates.add(activation.gate);
      } else {
        dormant++;
        dormantGates.add(activation.gate);
      }
    });

    return {
      dormant,
      defined,
      dormantGates: [...dormantGates].sort((a, b) => a - b),
      definedGates: [...definedGates].sort((a, b) => a - b),
    };
  }
}
